// flow-linker.ts — Pure function for detecting ActionFlow links to blocks
//
// Scans all flows for steps targeting a specific block by name.
// Used by HiddenBlockCard to show ActionFlow type badges.

import type { Flow } from '@cogo/types-v2'

// Types
export interface LinkedFlow {
  flowName: string
  flowId: string
  actionType: string
}

// Badge display mapping (per user decision: popup/drawer/toast/set_visible)
export const ACTION_BADGE_MAP: Record<string, { label: string; icon: string; color: string }> = {
  show_popup_action:   { label: 'popup',       icon: 'MessageSquare', color: 'bg-blue-100 text-blue-700' },
  open_drawer_action:  { label: 'drawer',      icon: 'PanelLeft',     color: 'bg-purple-100 text-purple-700' },
  show_toast_action:   { label: 'toast',       icon: 'Bell',          color: 'bg-amber-100 text-amber-700' },
  set_property_action: { label: 'set_visible', icon: 'Eye',           color: 'bg-primary/10 text-primary' },
}

// Pure function: scan all flows for steps targeting blockName
export function detectLinkedFlows(
  blockName: string,
  flows: Record<string, Flow>,
): LinkedFlow[] {
  const linked: LinkedFlow[] = []
  for (const [flowName, flow] of Object.entries(flows)) {
    if (!flow.steps) continue
    for (const step of flow.steps) {
      if (step.type === 'basic') {
        const target = (step.params?.target ?? step.params?.blockName) as string | undefined
        if (target === blockName && ACTION_BADGE_MAP[step.action]) {
          linked.push({ flowName, flowId: flowName, actionType: step.action })
        }
      }
    }
  }
  return linked
}

// Navigation helper: switch to flow editor and select specific flow
export function navigateToFlow(flowName: string): void {
  // Dynamic imports to avoid circular deps — these are store getState() calls
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { useStudioStore } = require('@/store/studio-store')
  const studioState = useStudioStore.getState()
  studioState.setCanvasView('flow')
  studioState.setIdeActiveTab('event-flow')
  studioState.setSlideOutOpen(true)

  // Select the flow in flow editor — use setTimeout to let the flow editor
  // mount and populate its flows list (it lazy-loads on canvasView switch)
  setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useFlowEditorStore } = require('@/features/flow-editor/store/flow-editor-store')
    const flowState = useFlowEditorStore.getState()
    const flow = flowState.flows.find(
      (f: { id: string; name: string }) => f.id === flowName || f.name === flowName,
    )
    if (flow) {
      flowState.selectFlow(flow.id)
    }
  }, 100)
}
